import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { gestApi, useGestAuth } from "./GestAuthContext";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import AlertsPanel from "./AlertsPanel";
import GestCronoprogramma from "./GestCronoprogramma";
import { CantiereForm } from "./GestCantieri";
import CostoForm from "./cantiere/CostoForm";
import RicaviCard from "./cantiere/RicaviCard";
import { categorieCosto, money } from "./cantiere/shared";

const lightColour = { green: "#16a34a", yellow: "#eab308", red: "#dc2626" };
const lightLabel = { green: "In linea", yellow: "Attenzione", red: "Critico" };
const statoLabel = { preventivo: "Preventivo", in_corso: "In corso", consegnato: "Consegnato", sospeso: "Sospeso" };
const dt = (s) => s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

function StatCard({ label, value, hint, tone = "light" }) {
  const bg = tone === "dark" ? "bg-[#0a1420] text-white" : "bg-white text-[#0a1420]";
  const sub = tone === "dark" ? "text-white/55" : "text-[#6e6e73]";
  return (
    <div className={`${bg} rounded-2xl p-5 border border-black/[0.04] shadow-sm`}>
      <div className={`text-[11px] uppercase tracking-widest ${sub}`}>{label}</div>
      <div className="mt-1 text-[22px] md:text-[26px] font-semibold tracking-[-0.02em]">{value}</div>
      {hint && <div className={`text-[12px] mt-0.5 ${sub}`}>{hint}</div>}
    </div>
  );
}

export default function GestCantiereDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { canSeeMoney, user } = useGestAuth();
  const canEdit = user?.role === "admin" || user?.role === "ufficio";
  const [cantiere, setCantiere] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prod, setProd] = useState([]);
  const [posa, setPosa] = useState([]);
  const [costi, setCosti] = useState([]);
  const [showCosto, setShowCosto] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, po] = await Promise.all([
        gestApi.get(`/cantieri/${id}`),
        gestApi.get(`/produzione?cantiere_id=${id}`),
        gestApi.get(`/posa?cantiere_id=${id}`),
      ]);
      setCantiere(c.data);
      setProd(p.data);
      setPosa(po.data);
      if (canSeeMoney) {
        const co = await gestApi.get(`/costi?cantiere_id=${id}`);
        setCosti(co.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  }, [id, canSeeMoney]);

  useEffect(() => { load(); }, [load]);

  const del = async () => {
    if (!window.confirm(`Eliminare il cantiere "${cantiere.nome}"? Verranno rimossi anche produzione, posa, costi e ricavi.`)) return;
    try {
      await gestApi.delete(`/cantieri/${id}`);
      toast.success("Cantiere eliminato");
      nav("/gestione/cantieri");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const delCosto = async (costoId) => {
    if (!window.confirm("Eliminare questo costo?")) return;
    try {
      await gestApi.delete(`/costi/${costoId}`);
      toast.success("Costo eliminato");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  if (loading) return <div className="p-8 text-[#6e6e73]">Caricamento…</div>;
  if (!cantiere) return <div className="p-8 text-red-600">Cantiere non trovato</div>;

  const kpi = cantiere.kpi || {};

  // Build cumulative chart data
  const events = [
    ...prod.slice().reverse().map((r) => ({ date: r.created_at.slice(0, 10), prod: r.n_prodotti, posa: 0 })),
    ...posa.slice().reverse().map((r) => ({ date: r.created_at.slice(0, 10), prod: 0, posa: r.n_posati })),
  ].sort((a, b) => a.date.localeCompare(b.date));
  let cp = 0, cpo = 0;
  const cumChart = events.map((e) => { cp += e.prod; cpo += e.posa; return { date: e.date, Produzione: cp, Posa: cpo }; });

  // Pie chart per categoria costi
  const costiPie = canSeeMoney ? Object.entries((cantiere.economics?.costi_per_categoria) || {}).map(([k, v]) => ({ name: categorieCosto.find(([kk]) => kk === k)?.[1] || k, value: v })) : [];
  const pieColours = ["#0a1420", "#a7c8e9", "#3b82f6", "#8b5cf6", "#ec4899", "#f97316", "#eab308", "#94a3b8"];

  return (
    <div className="p-5 md:p-8 max-w-[1400px] mx-auto" data-testid="gest-cantiere-detail">
      {/* Breadcrumb + header */}
      <div className="mb-6">
        <Link to="/gestione/cantieri" className="text-[12px] text-[#6e6e73] hover:text-[#0a1420]">‹ Cantieri</Link>
        <div className="flex items-start justify-between gap-4 mt-2 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: lightColour[kpi.light], boxShadow: `0 0 0 5px ${lightColour[kpi.light]}22` }} />
              <h1 className="text-[24px] md:text-[32px] font-semibold tracking-[-0.02em] text-[#0a1420]">{cantiere.nome}</h1>
            </div>
            <div className="text-[13px] text-[#6e6e73] mt-1">
              {cantiere.cliente} · {statoLabel[cantiere.stato]} · {lightLabel[kpi.light]}
              {kpi.days_to_deadline !== null && kpi.days_to_deadline !== undefined && (
                <> · {kpi.days_to_deadline >= 0 ? `${kpi.days_to_deadline}g alla consegna` : `In ritardo di ${-kpi.days_to_deadline}g`}</>
              )}
              {(cantiere.alerts?.length || 0) > 0 && (
                <button onClick={() => setTab("cronoprogramma")} className="ml-2 inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-medium" data-testid="gest-alerts-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
                  {cantiere.alerts.length} alert
                </button>
              )}
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button onClick={() => setShowEdit(true)} data-testid="gest-cantiere-edit" className="text-[12px] text-[#0a1420] border border-black/10 hover:bg-[#f5f7fb] rounded-lg px-3 py-1.5">Modifica</button>
              <button onClick={del} data-testid="gest-cantiere-delete" className="text-[12px] text-red-600 hover:text-red-700 border border-red-100 hover:border-red-200 rounded-lg px-3 py-1.5">Elimina cantiere</button>
            </div>
          )}
        </div>
      </div>

      {/* KPI */}
      <div className={`grid grid-cols-2 ${canSeeMoney ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3 md:gap-4 mb-6`}>
        <StatCard label="Produzione" value={`${kpi.prod_pct || 0}%`} hint={`${kpi.prodotti_totali || 0} / ${cantiere.n_infissi_totali || 0} pezzi`} />
        <StatCard label="Consegnati in cantiere" value={kpi.consegnati_totali || 0} hint={`da DDT uscita · ${cantiere.n_infissi_totali || 0} totali`} />
        <StatCard label="Posa" value={`${kpi.posa_pct || 0}%`} hint={`${kpi.posati_totali || 0} / ${cantiere.n_infissi_totali || 0} pezzi`} />
        {canSeeMoney && kpi.margine_pct !== undefined && (
          <StatCard label="Margine" value={`${kpi.margine_pct}%`} hint={money((kpi.ricavi_totali || 0) - (kpi.costi_totali || 0))} tone="dark" />
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 flex-wrap border-b border-black/5">
        {[
          ["overview", "Panoramica"],
          ["cronoprogramma", `Cronoprogramma${(cantiere.alerts || []).length ? ` · ${cantiere.alerts.length}` : ""}`],
          ["produzione", "Produzione"],
          ["posa", "Posa"],
          ...(canSeeMoney ? [["economia", "Economia"]] : []),
        ].map(([k, v]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`gest-tab-${k}`}
            className={`px-4 py-2 text-[13px] transition-colors border-b-2 -mb-px ${
              tab === k ? "text-[#0a1420] border-[#0a1420] font-semibold" : "text-[#6e6e73] border-transparent hover:text-[#0a1420]"
            }`}
          >{v}</button>
        ))}
      </div>

      {tab === "cronoprogramma" && (
        <GestCronoprogramma cantiere={cantiere} onCantiereRefresh={load} />
      )}

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-2xl p-5 border border-black/[0.04] shadow-sm">
            <div className="text-[13px] font-semibold text-[#0a1420] mb-3">Avanzamento cumulativo</div>
            {cumChart.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={cumChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6e6e73" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6e6e73" }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="Produzione" stroke="#a7c8e9" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Posa" stroke="#0a1420" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="text-[13px] text-[#6e6e73]">Ancora nessun avanzamento registrato.</div>}
          </div>
          <div className="bg-white rounded-2xl p-5 border border-black/[0.04] shadow-sm">
            <div className="text-[13px] font-semibold text-[#0a1420] mb-3">Dettagli</div>
            <dl className="space-y-2 text-[13px]">
              <div className="flex justify-between"><dt className="text-[#6e6e73]">Responsabile</dt><dd className="text-[#0a1420]">{cantiere.responsabile || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-[#6e6e73]">Indirizzo</dt><dd className="text-[#0a1420] text-right ml-4 truncate max-w-[60%]" title={cantiere.indirizzo}>{cantiere.indirizzo || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-[#6e6e73]">Inizio</dt><dd className="text-[#0a1420]">{dt(cantiere.data_inizio)}</dd></div>
              <div className="flex justify-between"><dt className="text-[#6e6e73]">Consegna</dt><dd className="text-[#0a1420]">{dt(cantiere.data_consegna_prevista)}</dd></div>
              <div className="flex justify-between"><dt className="text-[#6e6e73]">Infissi totali</dt><dd className="text-[#0a1420]">{cantiere.n_infissi_totali}</dd></div>
              {canSeeMoney && (
                <div className="flex justify-between"><dt className="text-[#6e6e73]">Valore commessa</dt><dd className="text-[#0a1420]">{money(cantiere.valore_commessa)}</dd></div>
              )}
            </dl>
            {cantiere.note && (
              <div className="mt-4 pt-3 border-t border-black/5">
                <div className="text-[11px] text-[#6e6e73] uppercase tracking-widest mb-1">Note</div>
                <div className="text-[13px] text-[#0a1420] whitespace-pre-wrap">{cantiere.note}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "produzione" && (
        <div className="bg-white rounded-2xl border border-black/[0.04] shadow-sm overflow-hidden" data-testid="gest-cantiere-produzione">
          <div className="px-5 py-3 border-b border-black/5 text-[13px] font-semibold text-[#0a1420]">Storico produzione ({prod.length})</div>
          {prod.length === 0 ? <div className="p-6 text-[13px] text-[#6e6e73] text-center">Nessun avanzamento produzione ancora.</div> : (
            <div className="divide-y divide-black/5">
              {prod.map((r) => (
                <div key={r.id} className="px-5 py-3 flex items-center gap-4">
                  <div className="w-16 text-[16px] font-semibold text-[#0a1420]">+{r.n_prodotti}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-[#0a1420] truncate">{r.autore_name}</div>
                    <div className="text-[12px] text-[#6e6e73] truncate">{r.note || "—"}</div>
                  </div>
                  <div className="text-[12px] text-[#6e6e73] shrink-0">{new Date(r.created_at).toLocaleString("it-IT")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "posa" && (
        <div className="bg-white rounded-2xl border border-black/[0.04] shadow-sm overflow-hidden" data-testid="gest-cantiere-posa">
          <div className="px-5 py-3 border-b border-black/5 text-[13px] font-semibold text-[#0a1420]">Storico posa ({posa.length})</div>
          {posa.length === 0 ? <div className="p-6 text-[13px] text-[#6e6e73] text-center">Nessun avanzamento posa ancora.</div> : (
            <div className="divide-y divide-black/5">
              {posa.map((r) => (
                <div key={r.id} className="px-5 py-3 flex items-center gap-4">
                  <div className="w-16 text-[16px] font-semibold text-[#0a1420]">+{r.n_posati}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-[#0a1420] truncate">{r.autore_name}</div>
                    <div className="text-[12px] text-[#6e6e73] truncate">{r.note || "—"}</div>
                  </div>
                  <div className="text-[12px] text-[#6e6e73] shrink-0">{new Date(r.created_at).toLocaleString("it-IT")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "economia" && canSeeMoney && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Cashflow snapshot */}
          <div className="lg:col-span-3 bg-white rounded-2xl p-5 border border-black/[0.04] shadow-sm" data-testid="gest-cashflow-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[13px] font-semibold text-[#0a1420]">Cashflow del cantiere</div>
              <Link to="/gestione/documenti" className="text-[12px] text-[#0d9488] font-semibold hover:underline">+ Registra fattura/incasso →</Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-[#f5f7fb] rounded-xl p-3">
                <div className="text-[10px] text-[#6e6e73] uppercase tracking-widest">Valore commessa</div>
                <div className="text-[16px] font-semibold text-[#0a1420] mt-0.5">{money(cantiere.valore_commessa)}</div>
              </div>
              <div className="bg-[#f5f7fb] rounded-xl p-3">
                <div className="text-[10px] text-[#6e6e73] uppercase tracking-widest">Fatturato</div>
                <div className="text-[16px] font-semibold text-[#0a1420] mt-0.5">{money(cantiere.economics?.fatturato_totale)}</div>
              </div>
              <div className="bg-[#0d9488]/5 rounded-xl p-3">
                <div className="text-[10px] text-[#0d9488] uppercase tracking-widest">Incassato</div>
                <div className="text-[16px] font-semibold text-[#0d9488] mt-0.5">{money(cantiere.economics?.incassato_totale)}</div>
              </div>
              <div className={`rounded-xl p-3 ${(cantiere.economics?.da_incassare || 0) > 0 ? "bg-orange-50" : "bg-green-50"}`}>
                <div className={`text-[10px] uppercase tracking-widest ${(cantiere.economics?.da_incassare || 0) > 0 ? "text-orange-600" : "text-green-700"}`}>Da incassare</div>
                <div className={`text-[16px] font-semibold mt-0.5 ${(cantiere.economics?.da_incassare || 0) > 0 ? "text-orange-600" : "text-green-700"}`}>{money(cantiere.economics?.da_incassare)}</div>
              </div>
              <div className="bg-red-50 rounded-xl p-3">
                <div className="text-[10px] text-red-600 uppercase tracking-widest">Speso</div>
                <div className="text-[16px] font-semibold text-red-600 mt-0.5">{money(cantiere.economics?.costi_totali)}</div>
              </div>
            </div>
            {/* Progress bars incassato & speso vs commessa */}
            {cantiere.valore_commessa > 0 && (
              <div className="mt-4 space-y-2">
                <div>
                  <div className="flex items-center justify-between text-[10.5px] text-[#6e6e73] mb-0.5">
                    <span>Incassato sul valore commessa</span>
                    <span className="font-medium">{Math.round((cantiere.economics?.incassato_totale || 0) / cantiere.valore_commessa * 100)}%</span>
                  </div>
                  <div className="h-2 bg-[#f5f7fb] rounded-full overflow-hidden">
                    <div className="h-2 bg-[#0d9488]" style={{ width: `${Math.min(100, ((cantiere.economics?.incassato_totale || 0) / cantiere.valore_commessa) * 100)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-[10.5px] text-[#6e6e73] mb-0.5">
                    <span>Speso sul valore commessa</span>
                    <span className="font-medium">{Math.round((cantiere.economics?.costi_totali || 0) / cantiere.valore_commessa * 100)}%</span>
                  </div>
                  <div className="h-2 bg-[#f5f7fb] rounded-full overflow-hidden">
                    <div className="h-2 bg-red-500" style={{ width: `${Math.min(100, ((cantiere.economics?.costi_totali || 0) / cantiere.valore_commessa) * 100)}%` }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <RicaviCard cantiereId={id} onChanged={load} />
          <div className="bg-white rounded-2xl p-5 border border-black/[0.04] shadow-sm lg:col-span-2">
            <div className="text-[13px] font-semibold text-[#0a1420] mb-3">Ripartizione costi</div>
            {costiPie.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={costiPie} dataKey="value" innerRadius={50} outerRadius={80} paddingAngle={3}>
                    {costiPie.map((e, i) => <Cell key={e.name || i} fill={pieColours[i % pieColours.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => money(v)} />
                  <Legend iconType="circle" verticalAlign="bottom" formatter={(v, e) => <span className="text-[12px] text-[#6e6e73]">{v} — {money(e.payload.value)}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-[13px] text-[#6e6e73]">Nessun costo registrato.</div>}
          </div>
          <div className="lg:col-span-3 bg-white rounded-2xl border border-black/[0.04] shadow-sm overflow-hidden" data-testid="gest-costi-list">
            <div className="px-5 py-3 border-b border-black/5 flex items-center justify-between">
              <div className="text-[13px] font-semibold text-[#0a1420]">Costi ({costi.length})</div>
              {canEdit && (
                <button onClick={() => setShowCosto(true)} data-testid="gest-costo-new" className="px-3 py-1.5 bg-[#0a1420] text-white rounded-lg text-[12px] font-semibold">+ Nuovo costo</button>
              )}
            </div>
            {costi.length === 0 ? <div className="p-6 text-[13px] text-[#6e6e73] text-center">Nessun costo registrato.</div> : (
              <div className="divide-y divide-black/5">
                {costi.map((c) => (
                  <div key={c.id} className="px-5 py-3 grid grid-cols-[1fr_auto_auto] gap-3 items-center">
                    <div className="min-w-0">
                      <div className="text-[13px] text-[#0a1420] truncate">{c.fornitore}</div>
                      <div className="text-[11px] text-[#6e6e73] truncate">{categorieCosto.find(([k]) => k === c.categoria)?.[1] || c.categoria} · {c.tipo_documento}{c.numero_documento ? ` ${c.numero_documento}` : ""} · {dt(c.data)}</div>
                    </div>
                    <div className="text-[14px] font-semibold text-[#0a1420]">{`€ ${Number(c.importo || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</div>
                    {canEdit && <button onClick={() => delCosto(c.id)} data-testid={`gest-costo-del-${c.id}`} className="text-[11px] text-red-500 hover:text-red-700">Elimina</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showCosto && (
        <CostoForm cantiereId={id} onClose={() => setShowCosto(false)} onSaved={load} />
      )}
      {showEdit && (
        <CantiereForm
          initial={cantiere}
          onClose={() => setShowEdit(false)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}
